import { useEffect } from 'react';

import { SLOW_SELECTION_MS } from '../config/slownessConfig';
import type { DiagramSlowness } from '../types/DiagramSlowness.types';
import { afterNextPaint } from '../utils/afterNextPaint';

type UseSlowSelectionDetectionParams = {
  /** The series selector; only clicks on its checkboxes count. */
  selectorElement: HTMLElement | null;
  onSlowSelection: (slowness: Extract<DiagramSlowness, { kind: 'selection' }>) => void;
};

function isCheckbox(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement && target.type === 'checkbox';
}

/**
 * Times a click on a series checkbox until the tick is painted. Listening in the capture phase runs
 * before React handles the click, so the time already gone by then is what the click waited for.
 */
export function useSlowSelectionDetection({ selectorElement, onSlowSelection }: UseSlowSelectionDetectionParams) {
  useEffect(() => {
    if (!selectorElement) {
      return undefined;
    }

    const pendingWaits = new Set<() => void>();

    function handleClick(event: MouseEvent) {
      if (!isCheckbox(event.target) || document.hidden) {
        return;
      }

      // Event time stamps share the clock of performance.now().
      const clickedAt = event.timeStamp;
      const inputDelayMs = performance.now() - clickedAt;
      const cancelWait = afterNextPaint(() => {
        pendingWaits.delete(cancelWait);

        const durationMs = performance.now() - clickedAt;

        if (durationMs > SLOW_SELECTION_MS && !document.hidden) {
          onSlowSelection({ kind: 'selection', durationMs, inputDelayMs });
        }
      });

      pendingWaits.add(cancelWait);
    }

    selectorElement.addEventListener('click', handleClick, { capture: true });

    return () => {
      selectorElement.removeEventListener('click', handleClick, { capture: true });
      pendingWaits.forEach((cancelWait) => cancelWait());
    };
  }, [selectorElement, onSlowSelection]);
}
